import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Palette, 
  Image, 
  Type, 
  Save, 
  Loader2, 
  RefreshCw,
  Settings,
  FileText,
  Upload,
  Download,
  LogIn,
  AlertTriangle,
  Tag,
  Plus,
  Trash2,
  X,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const StylePage = () => {
  const { api, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingManual, setUploadingManual] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [manualInfo, setManualInfo] = useState(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [restoreType, setRestoreType] = useState(null);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const loginLogoInputRef = useRef(null);
  const pdfLogoInputRef = useRef(null);
  
  // PDF Responsiva Config - Todos los campos configurables
  const [pdfConfig, setPdfConfig] = useState({
    // Company Info
    logo_url: '',
    company_name: 'CM Idiomas',
    company_address: '',
    company_phone: '',
    company_email: '',
    // Colors
    header_color: '#00B2A9',
    // Text
    disclaimer_text: 'Por este conducto, la persona receptora se hace responsable de este equipo y se obliga a devolverlo al equipo técnico de CM Idiomas al término del servicio en las mismas condiciones que lo recibió.',
    // Columns
    show_quantity: true,
    show_description: true,
    show_serial: false,
    show_location: false,
    show_condition: false,
    custom_columns: [],
    // Signature fields
    signature_fields: [
      { label: 'ENTREGA - CM IDIOMAS', type: 'entrega' },
      { label: 'RECIBE - CLIENTE', type: 'recibe' }
    ]
  });
  const [savingPdf, setSavingPdf] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [newSignatureLabel, setNewSignatureLabel] = useState('');
  
  const defaultConfig = {
    // App Identity
    app_name: 'Entradas y Salidas de Equipo',
    company_name: 'CM Idiomas',
    logo_url: '',
    logo_size: '48',
    favicon_url: '',
    
    // Colors - Main
    primary_color: '#00B2A9',
    secondary_color: '#10B981',
    accent_color: '#3B82F6',
    background_color: '#0a0a0a',
    card_background: '#1a1a1a',
    text_color: '#ffffff',
    muted_color: '#a1a1aa',
    
    // Colors - Status Badges
    color_disponible: '#10B981',
    color_asignado: '#3B82F6',
    color_prestamo: '#8B5CF6',
    color_mantenimiento: '#F59E0B',
    color_falla: '#EF4444',
    color_perdido: '#DC2626',
    
    // Typography
    font_family: 'Inter, system-ui, sans-serif',
    font_size_base: '14',
    font_size_heading: '24',
    font_weight_normal: '400',
    font_weight_bold: '600',
    
    // Layout
    sidebar_width: '240',
    border_radius: '8',
    spacing_unit: '16',
    
    // Text Labels - Navigation
    label_dashboard: 'Dashboard',
    label_inventory: 'Inventario',
    label_movements: 'Movimientos',
    label_tracking: 'Rastreo',
    label_calendar: 'Calendario',
    label_reports: 'Reportes',
    label_users: 'Usuarios',
    label_responsibles: 'Responsables',
    label_categories: 'Categorías',
    label_locations: 'Ubicaciones',
    
    // Text Labels - Actions
    label_salida: 'Salida',
    label_retorno: 'Retorno',
    label_prestamo: 'Préstamo',
    label_evento: 'Evento',
    
    // Text Labels - Status
    label_status_disponible: 'Disponible',
    label_status_asignado: 'Asignado',
    label_status_prestamo: 'En Préstamo',
    label_status_mantenimiento: 'Mantenimiento',
    label_status_falla: 'Con Falla',
    label_status_perdido: 'Perdido',
    
    // Footer
    footer_text: '© 2024 CM Idiomas. Todos los derechos reservados.',
    
    // Custom CSS
    custom_css: ''
  };

  const defaultLoginConfig = {
    login_logo_url: '',
    login_background_color: '#0a0a0a',
    login_card_color: '#1a1a1a',
    login_title: 'Entradas y Salidas de Equipo',
    login_subtitle: 'CM Idiomas',
    login_welcome_text: 'Ingresa tus credenciales para acceder',
    login_button_text: 'Ingresar',
    login_button_color: '#00B2A9',
    login_text_color: '#ffffff'
  };

  const [config, setConfig] = useState(defaultConfig);
  const [loginConfig, setLoginConfig] = useState(defaultLoginConfig);

  useEffect(() => {
    loadConfig();
    loadManualInfo();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const apiInstance = api();
      const [styleResponse, loginResponse, pdfResponse] = await Promise.all([
        apiInstance.get('/config/style'),
        apiInstance.get('/config/login').catch(() => ({ data: {} })),
        apiInstance.get('/config/pdf-template').catch(() => ({ data: {} }))
      ]);
      
      if (styleResponse.data) {
        setConfig(prev => ({ ...prev, ...styleResponse.data }));
      }
      if (loginResponse.data) {
        setLoginConfig(prev => ({ ...prev, ...loginResponse.data }));
      }
      if (pdfResponse.data && Object.keys(pdfResponse.data).length > 0) {
        setPdfConfig(prev => ({ ...prev, ...pdfResponse.data }));
      }
    } catch (error) {
      console.log('Using default config');
    } finally {
      setLoading(false);
    }
  };

  const loadManualInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/manual/info`);
      if (response.ok) {
        const data = await response.json();
        setManualInfo(data);
      }
    } catch (error) {
      console.log('Manual info not available');
    }
  };

  const handleUploadLogo = async (event, type = 'app') => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no puede superar 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const apiInstance = api();
      const response = await apiInstance.post('/config/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // The response now contains a data URL (Base64) that can be used directly
      const logoUrl = response.data.url;
      
      if (type === 'login') {
        setLoginConfig(prev => ({ ...prev, login_logo_url: logoUrl }));
      } else {
        // When uploading app logo, also set as login logo if login logo is empty
        setConfig(prev => ({ ...prev, logo_url: logoUrl }));
        setLoginConfig(prev => ({
          ...prev,
          login_logo_url: prev.login_logo_url || logoUrl
        }));
      }
      
      toast.success('Logo subido exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el logo');
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
      if (loginLogoInputRef.current) loginLogoInputRef.current.value = '';
    }
  };

  const handleUploadManual = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no puede superar 10MB');
      return;
    }

    setUploadingManual(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const apiInstance = api();
      await apiInstance.post('/manual/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Manual subido exitosamente');
      loadManualInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el manual');
    } finally {
      setUploadingManual(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRestoreDefaultManual = async () => {
    try {
      const apiInstance = api();
      await apiInstance.post('/manual/restore-default');
      toast.success('Manual restaurado al predeterminado');
      loadManualInfo();
      setShowRestoreDialog(false);
    } catch (error) {
      toast.error('Error al restaurar el manual');
    }
  };

  const handleDownloadManual = async () => {
    try {
      const response = await fetch(`${API_URL}/api/manual/download`);
      if (!response.ok) throw new Error('Error');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Manual_Usuario_CM_Idiomas.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error('Error al descargar');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const apiInstance = api();
      await Promise.all([
        apiInstance.put('/config/style', config),
        apiInstance.put('/config/login', loginConfig)
      ]);
      toast.success('Configuración guardada');
      applyStyles(config);
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const applyStyles = (cfg) => {
    document.documentElement.style.setProperty('--primary', cfg.primary_color);
    document.documentElement.style.setProperty('--secondary', cfg.secondary_color);
    document.documentElement.style.setProperty('--accent', cfg.accent_color);
    localStorage.setItem('app_style_config', JSON.stringify(cfg));
  };

  const confirmRestore = (type) => {
    setRestoreType(type);
    setShowRestoreDialog(true);
  };

  const executeRestore = () => {
    if (restoreType === 'style') {
      setConfig(defaultConfig);
      toast.info('Estilos restaurados a valores por defecto');
    } else if (restoreType === 'login') {
      setLoginConfig(defaultLoginConfig);
      toast.info('Configuración de login restaurada');
    } else if (restoreType === 'manual') {
      handleRestoreDefaultManual();
      return;
    }
    setShowRestoreDialog(false);
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <Settings className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-bold mb-2">Acceso Restringido</h2>
          <p className="text-muted-foreground">
            Solo los superadministradores pueden acceder a esta configuración.
          </p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="h-96 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="style-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Palette className="h-8 w-8 text-primary" />
            Personalización de Estilo
          </h1>
          <p className="text-muted-foreground mt-1">
            Personaliza la apariencia y nombres de la aplicación
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Guardar Cambios
        </Button>
      </div>

      <Tabs defaultValue="login" className="space-y-6">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1">
          <TabsTrigger value="login" className="flex items-center gap-2 px-3 py-2">
            <LogIn className="h-4 w-4" />
            <span className="hidden sm:inline">Login</span>
          </TabsTrigger>
          <TabsTrigger value="identity" className="flex items-center gap-2 px-3 py-2">
            <Image className="h-4 w-4" />
            <span className="hidden sm:inline">Identidad</span>
          </TabsTrigger>
          <TabsTrigger value="colors" className="flex items-center gap-2 px-3 py-2">
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">Colores</span>
          </TabsTrigger>
          <TabsTrigger value="status" className="flex items-center gap-2 px-3 py-2">
            <Tag className="h-4 w-4" />
            <span className="hidden sm:inline">Estados</span>
          </TabsTrigger>
          <TabsTrigger value="typography" className="flex items-center gap-2 px-3 py-2">
            <Type className="h-4 w-4" />
            <span className="hidden sm:inline">Tipografía</span>
          </TabsTrigger>
          <TabsTrigger value="labels" className="flex items-center gap-2 px-3 py-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Etiquetas</span>
          </TabsTrigger>
          <TabsTrigger value="manual" className="flex items-center gap-2 px-3 py-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Manual</span>
          </TabsTrigger>
          <TabsTrigger value="pdf" className="flex items-center gap-2 px-3 py-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">PDF</span>
          </TabsTrigger>
          <TabsTrigger value="advanced" className="flex items-center gap-2 px-3 py-2">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Avanzado</span>
          </TabsTrigger>
        </TabsList>

        {/* Login Tab */}
        <TabsContent value="login">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Pantalla de Login</CardTitle>
                <CardDescription>
                  Personaliza la apariencia de la pantalla de inicio de sesión
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Logo Upload */}
                <div className="space-y-2">
                  <Label>Logo del Login</Label>
                  <div className="flex gap-2">
                    <Input
                      value={loginConfig.login_logo_url}
                      onChange={(e) => setLoginConfig({ ...loginConfig, login_logo_url: e.target.value })}
                      placeholder="URL del logo o sube un archivo"
                      className="flex-1"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUploadLogo(e, 'login')}
                      ref={loginLogoInputRef}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => loginLogoInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    </Button>
                  </div>
                  {loginConfig.login_logo_url && (
                    <div className="mt-2 p-3 bg-muted/30 rounded-lg">
                      <img 
                        src={loginConfig.login_logo_url} 
                        alt="Logo preview" 
                        className="max-h-16 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>

                {/* Texts */}
                <div className="space-y-2">
                  <Label>Título Principal</Label>
                  <Input
                    value={loginConfig.login_title}
                    onChange={(e) => setLoginConfig({ ...loginConfig, login_title: e.target.value })}
                    placeholder="Entradas y Salidas de Equipo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subtítulo</Label>
                  <Input
                    value={loginConfig.login_subtitle}
                    onChange={(e) => setLoginConfig({ ...loginConfig, login_subtitle: e.target.value })}
                    placeholder="CM Idiomas"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Texto de Bienvenida</Label>
                  <Input
                    value={loginConfig.login_welcome_text}
                    onChange={(e) => setLoginConfig({ ...loginConfig, login_welcome_text: e.target.value })}
                    placeholder="Ingresa tus credenciales para acceder"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Texto del Botón</Label>
                  <Input
                    value={loginConfig.login_button_text}
                    onChange={(e) => setLoginConfig({ ...loginConfig, login_button_text: e.target.value })}
                    placeholder="Ingresar"
                  />
                </div>

                {/* Colors */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fondo</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={loginConfig.login_background_color}
                        onChange={(e) => setLoginConfig({ ...loginConfig, login_background_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={loginConfig.login_background_color}
                        onChange={(e) => setLoginConfig({ ...loginConfig, login_background_color: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Tarjeta</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={loginConfig.login_card_color}
                        onChange={(e) => setLoginConfig({ ...loginConfig, login_card_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={loginConfig.login_card_color}
                        onChange={(e) => setLoginConfig({ ...loginConfig, login_card_color: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Botón</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={loginConfig.login_button_color}
                        onChange={(e) => setLoginConfig({ ...loginConfig, login_button_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={loginConfig.login_button_color}
                        onChange={(e) => setLoginConfig({ ...loginConfig, login_button_color: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Texto</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={loginConfig.login_text_color}
                        onChange={(e) => setLoginConfig({ ...loginConfig, login_text_color: e.target.value })}
                        className="w-12 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={loginConfig.login_text_color}
                        onChange={(e) => setLoginConfig({ ...loginConfig, login_text_color: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Restore Button */}
                <div className="pt-4 border-t">
                  <Button variant="outline" onClick={() => confirmRestore('login')} className="w-full">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Restaurar Login a Valores Predeterminados
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Preview Card */}
            <Card>
              <CardHeader>
                <CardTitle>Vista Previa</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="rounded-lg p-8 min-h-[400px] flex items-center justify-center"
                  style={{ backgroundColor: loginConfig.login_background_color }}
                >
                  <div 
                    className="w-full max-w-sm p-6 rounded-xl shadow-xl"
                    style={{ backgroundColor: loginConfig.login_card_color }}
                  >
                    {loginConfig.login_logo_url && (
                      <div className="flex justify-center mb-4">
                        <img 
                          src={loginConfig.login_logo_url} 
                          alt="Logo" 
                          className="max-h-16 object-contain"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                    <h2 
                      className="text-xl font-bold text-center mb-1"
                      style={{ color: loginConfig.login_text_color }}
                    >
                      {loginConfig.login_title}
                    </h2>
                    <p 
                      className="text-center text-sm mb-4"
                      style={{ color: loginConfig.login_text_color, opacity: 0.7 }}
                    >
                      {loginConfig.login_subtitle}
                    </p>
                    <p 
                      className="text-center text-xs mb-4"
                      style={{ color: loginConfig.login_text_color, opacity: 0.5 }}
                    >
                      {loginConfig.login_welcome_text}
                    </p>
                    <div className="space-y-3">
                      <div className="h-10 bg-white/10 rounded-lg" />
                      <div className="h-10 bg-white/10 rounded-lg" />
                      <button
                        className="w-full h-10 rounded-lg font-medium text-white"
                        style={{ backgroundColor: loginConfig.login_button_color }}
                      >
                        {loginConfig.login_button_text}
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Identity Tab */}
        <TabsContent value="identity">
          <Card>
            <CardHeader>
              <CardTitle>Identidad de la Aplicación</CardTitle>
              <CardDescription>
                Configura el nombre, logo y marca de la aplicación
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Nombre de la Aplicación</Label>
                  <Input
                    value={config.app_name}
                    onChange={(e) => setConfig({ ...config, app_name: e.target.value })}
                    placeholder="Ej: Entradas y Salidas de Equipo"
                  />
                  <p className="text-xs text-muted-foreground">
                    Se muestra en el header y título de la página
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Nombre de la Empresa</Label>
                  <Input
                    value={config.company_name}
                    onChange={(e) => setConfig({ ...config, company_name: e.target.value })}
                    placeholder="Ej: CM Idiomas"
                  />
                  <p className="text-xs text-muted-foreground">
                    Se muestra en el footer y PDFs
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Logo de la Aplicación</Label>
                  <div className="flex gap-2">
                    <Input
                      value={config.logo_url}
                      onChange={(e) => setConfig({ ...config, logo_url: e.target.value })}
                      placeholder="URL del logo o sube un archivo"
                      className="flex-1"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUploadLogo(e, 'app')}
                      ref={logoInputRef}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    </Button>
                  </div>
                  {config.logo_url && (
                    <div className="mt-2 p-4 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-2">Vista previa:</p>
                      <img 
                        src={config.logo_url} 
                        alt="Logo preview" 
                        style={{ height: `${config.logo_size || 48}px` }}
                        className="object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                  {/* Logo Size Control */}
                  <div className="mt-3 space-y-2">
                    <Label>Tamaño del Logo en Sidebar</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min="24"
                        max="160"
                        step="2"
                        value={config.logo_size || '48'}
                        onChange={(e) => setConfig({ ...config, logo_size: e.target.value })}
                        className="flex-1 accent-primary h-2 cursor-pointer"
                        data-testid="logo-size-slider"
                      />
                      <span className="text-sm font-mono w-14 text-center bg-muted/50 rounded px-2 py-1">
                        {config.logo_size || '48'}px
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Ajusta la altura del logo en la barra lateral (24px - 160px)
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>URL del Favicon</Label>
                  <Input
                    value={config.favicon_url}
                    onChange={(e) => setConfig({ ...config, favicon_url: e.target.value })}
                    placeholder="https://ejemplo.com/favicon.ico"
                  />
                  <p className="text-xs text-muted-foreground">
                    Icono que aparece en la pestaña del navegador
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Texto del Footer</Label>
                <Input
                  value={config.footer_text}
                  onChange={(e) => setConfig({ ...config, footer_text: e.target.value })}
                  placeholder="© 2024 Tu Empresa. Todos los derechos reservados."
                />
              </div>

              {/* Restore Button */}
              <div className="pt-4 border-t">
                <Button variant="outline" onClick={() => confirmRestore('style')}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Restaurar Identidad a Valores Predeterminados
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Colors Tab */}
        <TabsContent value="colors">
          <Card>
            <CardHeader>
              <CardTitle>Paleta de Colores</CardTitle>
              <CardDescription>
                Personaliza los colores principales de la aplicación
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <Label>Color Primario</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.primary_color}
                      onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={config.primary_color}
                      onChange={(e) => setConfig({ ...config, primary_color: e.target.value })}
                      placeholder="#00B2A9"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Botones, links activos</p>
                </div>

                <div className="space-y-2">
                  <Label>Color Secundario</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.secondary_color}
                      onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={config.secondary_color}
                      onChange={(e) => setConfig({ ...config, secondary_color: e.target.value })}
                      placeholder="#10B981"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Badges de éxito</p>
                </div>

                <div className="space-y-2">
                  <Label>Color de Acento</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.accent_color}
                      onChange={(e) => setConfig({ ...config, accent_color: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={config.accent_color}
                      onChange={(e) => setConfig({ ...config, accent_color: e.target.value })}
                      placeholder="#3B82F6"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Elementos destacados</p>
                </div>

                <div className="space-y-2">
                  <Label>Fondo Principal</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.background_color}
                      onChange={(e) => setConfig({ ...config, background_color: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={config.background_color}
                      onChange={(e) => setConfig({ ...config, background_color: e.target.value })}
                      placeholder="#0a0a0a"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Fondo de Tarjetas</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.card_background}
                      onChange={(e) => setConfig({ ...config, card_background: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={config.card_background}
                      onChange={(e) => setConfig({ ...config, card_background: e.target.value })}
                      placeholder="#1a1a1a"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Color de Texto</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.text_color}
                      onChange={(e) => setConfig({ ...config, text_color: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={config.text_color}
                      onChange={(e) => setConfig({ ...config, text_color: e.target.value })}
                      placeholder="#ffffff"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Color Preview */}
              <div className="mt-6 p-6 rounded-lg border" style={{ 
                backgroundColor: config.background_color,
                color: config.text_color
              }}>
                <h3 className="text-lg font-bold mb-4">Vista Previa</h3>
                <div className="flex gap-4 flex-wrap">
                  <button 
                    className="px-4 py-2 rounded-lg font-medium text-white"
                    style={{ backgroundColor: config.primary_color }}
                  >
                    Botón Primario
                  </button>
                  <button 
                    className="px-4 py-2 rounded-lg font-medium text-white"
                    style={{ backgroundColor: config.secondary_color }}
                  >
                    Botón Secundario
                  </button>
                  <button 
                    className="px-4 py-2 rounded-lg font-medium text-white"
                    style={{ backgroundColor: config.accent_color }}
                  >
                    Acento
                  </button>
                </div>
                <div 
                  className="mt-4 p-4 rounded-lg"
                  style={{ backgroundColor: config.card_background }}
                >
                  <p>Esto es una tarjeta de ejemplo</p>
                  <p style={{ color: config.muted_color }} className="text-sm mt-1">
                    Texto secundario
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Status Colors Tab */}
        <TabsContent value="status">
          <Card>
            <CardHeader>
              <CardTitle>Colores de Estados</CardTitle>
              <CardDescription>
                Personaliza los colores de cada estado de equipo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color_disponible }} />
                    Disponible
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.color_disponible || '#10B981'}
                      onChange={(e) => setConfig({ ...config, color_disponible: e.target.value })}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={config.color_disponible || '#10B981'}
                      onChange={(e) => setConfig({ ...config, color_disponible: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color_asignado }} />
                    Asignado
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.color_asignado || '#3B82F6'}
                      onChange={(e) => setConfig({ ...config, color_asignado: e.target.value })}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={config.color_asignado || '#3B82F6'}
                      onChange={(e) => setConfig({ ...config, color_asignado: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color_prestamo }} />
                    En Préstamo
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.color_prestamo || '#8B5CF6'}
                      onChange={(e) => setConfig({ ...config, color_prestamo: e.target.value })}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={config.color_prestamo || '#8B5CF6'}
                      onChange={(e) => setConfig({ ...config, color_prestamo: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color_mantenimiento }} />
                    Mantenimiento
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.color_mantenimiento || '#F59E0B'}
                      onChange={(e) => setConfig({ ...config, color_mantenimiento: e.target.value })}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={config.color_mantenimiento || '#F59E0B'}
                      onChange={(e) => setConfig({ ...config, color_mantenimiento: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color_falla }} />
                    Con Falla
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.color_falla || '#EF4444'}
                      onChange={(e) => setConfig({ ...config, color_falla: e.target.value })}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={config.color_falla || '#EF4444'}
                      onChange={(e) => setConfig({ ...config, color_falla: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color_perdido }} />
                    Perdido
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={config.color_perdido || '#DC2626'}
                      onChange={(e) => setConfig({ ...config, color_perdido: e.target.value })}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={config.color_perdido || '#DC2626'}
                      onChange={(e) => setConfig({ ...config, color_perdido: e.target.value })}
                      className="font-mono"
                    />
                  </div>
                </div>
              </div>
              
              {/* Preview */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <Label className="mb-3 block">Vista Previa de Badges</Label>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 rounded text-white text-sm" style={{ backgroundColor: config.color_disponible }}>Disponible</span>
                  <span className="px-2 py-1 rounded text-white text-sm" style={{ backgroundColor: config.color_asignado }}>Asignado</span>
                  <span className="px-2 py-1 rounded text-white text-sm" style={{ backgroundColor: config.color_prestamo }}>En Préstamo</span>
                  <span className="px-2 py-1 rounded text-white text-sm" style={{ backgroundColor: config.color_mantenimiento }}>Mantenimiento</span>
                  <span className="px-2 py-1 rounded text-white text-sm" style={{ backgroundColor: config.color_falla }}>Con Falla</span>
                  <span className="px-2 py-1 rounded text-white text-sm" style={{ backgroundColor: config.color_perdido }}>Perdido</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Typography Tab */}
        <TabsContent value="typography">
          <Card>
            <CardHeader>
              <CardTitle>Tipografía</CardTitle>
              <CardDescription>
                Configura las fuentes y tamaños de texto
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Familia de Fuentes</Label>
                  <select
                    value={config.font_family || 'Inter, system-ui, sans-serif'}
                    onChange={(e) => setConfig({ ...config, font_family: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  >
                    <option value="Inter, system-ui, sans-serif">Inter (Moderna)</option>
                    <option value="'Roboto', sans-serif">Roboto</option>
                    <option value="'Open Sans', sans-serif">Open Sans</option>
                    <option value="'Lato', sans-serif">Lato</option>
                    <option value="'Poppins', sans-serif">Poppins</option>
                    <option value="'Montserrat', sans-serif">Montserrat</option>
                    <option value="system-ui, sans-serif">Sistema</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label>Tamaño Base (px)</Label>
                  <Input
                    type="number"
                    min="12"
                    max="18"
                    value={config.font_size_base || '14'}
                    onChange={(e) => setConfig({ ...config, font_size_base: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Tamaño Encabezados (px)</Label>
                  <Input
                    type="number"
                    min="18"
                    max="36"
                    value={config.font_size_heading || '24'}
                    onChange={(e) => setConfig({ ...config, font_size_heading: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Peso Normal</Label>
                  <select
                    value={config.font_weight_normal || '400'}
                    onChange={(e) => setConfig({ ...config, font_weight_normal: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  >
                    <option value="300">Ligero (300)</option>
                    <option value="400">Normal (400)</option>
                    <option value="500">Medio (500)</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label>Peso Bold</Label>
                  <select
                    value={config.font_weight_bold || '600'}
                    onChange={(e) => setConfig({ ...config, font_weight_bold: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                  >
                    <option value="500">Medio (500)</option>
                    <option value="600">Semibold (600)</option>
                    <option value="700">Bold (700)</option>
                    <option value="800">Extrabold (800)</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label>Border Radius (px)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="20"
                    value={config.border_radius || '8'}
                    onChange={(e) => setConfig({ ...config, border_radius: e.target.value })}
                  />
                </div>
              </div>
              
              {/* Typography Preview */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-3" style={{ fontFamily: config.font_family }}>
                <Label className="mb-3 block">Vista Previa</Label>
                <h2 style={{ fontSize: `${config.font_size_heading}px`, fontWeight: config.font_weight_bold }}>
                  Encabezado de Ejemplo
                </h2>
                <p style={{ fontSize: `${config.font_size_base}px`, fontWeight: config.font_weight_normal }}>
                  Este es un texto de ejemplo con el tamaño y peso normal configurado. 
                  <strong style={{ fontWeight: config.font_weight_bold }}> Este texto está en negrita.</strong>
                </p>
                <div className="flex gap-2">
                  <button 
                    className="px-4 py-2 bg-primary text-white" 
                    style={{ borderRadius: `${config.border_radius}px`, fontSize: `${config.font_size_base}px` }}
                  >
                    Botón Ejemplo
                  </button>
                  <button 
                    className="px-4 py-2 border border-current" 
                    style={{ borderRadius: `${config.border_radius}px`, fontSize: `${config.font_size_base}px` }}
                  >
                    Botón Outline
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Labels Tab */}
        <TabsContent value="labels">
          <Card>
            <CardHeader>
              <CardTitle>Etiquetas y Nombres</CardTitle>
              <CardDescription>
                Personaliza los nombres de las secciones y elementos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Dashboard</Label>
                  <Input
                    value={config.label_dashboard}
                    onChange={(e) => setConfig({ ...config, label_dashboard: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Inventario</Label>
                  <Input
                    value={config.label_inventory}
                    onChange={(e) => setConfig({ ...config, label_inventory: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Movimientos</Label>
                  <Input
                    value={config.label_movements}
                    onChange={(e) => setConfig({ ...config, label_movements: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Salida</Label>
                  <Input
                    value={config.label_salida}
                    onChange={(e) => setConfig({ ...config, label_salida: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Retorno</Label>
                  <Input
                    value={config.label_retorno}
                    onChange={(e) => setConfig({ ...config, label_retorno: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reportes</Label>
                  <Input
                    value={config.label_reports}
                    onChange={(e) => setConfig({ ...config, label_reports: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Calendario</Label>
                  <Input
                    value={config.label_calendar}
                    onChange={(e) => setConfig({ ...config, label_calendar: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Usuarios</Label>
                  <Input
                    value={config.label_users}
                    onChange={(e) => setConfig({ ...config, label_users: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Responsables</Label>
                  <Input
                    value={config.label_responsibles}
                    onChange={(e) => setConfig({ ...config, label_responsibles: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categorías</Label>
                  <Input
                    value={config.label_categories}
                    onChange={(e) => setConfig({ ...config, label_categories: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ubicaciones</Label>
                  <Input
                    value={config.label_locations}
                    onChange={(e) => setConfig({ ...config, label_locations: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Etiquetas QR</Label>
                  <Input
                    value={config.label_etiquetas || 'Etiquetas QR'}
                    onChange={(e) => setConfig({ ...config, label_etiquetas: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manual Tab */}
        <TabsContent value="manual">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Manual de Usuario
              </CardTitle>
              <CardDescription>
                Sube un manual de usuario personalizado en formato PDF
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Manual Info */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Manual Actual</p>
                    <p className="text-sm text-muted-foreground">
                      {manualInfo?.is_custom ? 'Manual personalizado' : 'Manual predeterminado del sistema'}
                    </p>
                    {manualInfo && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Tamaño: {(manualInfo.size / 1024).toFixed(1)} KB • 
                        Actualizado: {manualInfo.modified ? new Date(manualInfo.modified).toLocaleDateString() : 'N/A'}
                      </p>
                    )}
                  </div>
                  <Button variant="outline" onClick={handleDownloadManual}>
                    <Download className="h-4 w-4 mr-2" />
                    Descargar
                  </Button>
                </div>
              </div>

              {/* Upload New Manual */}
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleUploadManual}
                  ref={fileInputRef}
                  className="hidden"
                  id="manual-upload"
                />
                <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium mb-2">Subir Nuevo Manual</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Arrastra un archivo PDF aquí o haz clic para seleccionar
                </p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingManual}
                >
                  {uploadingManual ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Seleccionar Archivo PDF
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-3">
                  Tamaño máximo: 10MB • Solo archivos PDF
                </p>
              </div>

              {/* Restore Default */}
              {manualInfo?.is_custom && (
                <div className="pt-4 border-t">
                  <Button variant="outline" onClick={() => confirmRestore('manual')}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Restaurar Manual Predeterminado
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Advanced Tab */}
        <TabsContent value="advanced">
          <Card>
            <CardHeader>
              <CardTitle>Configuración Avanzada</CardTitle>
              <CardDescription>
                CSS personalizado y configuraciones avanzadas
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>CSS Personalizado</Label>
                <Textarea
                  value={config.custom_css}
                  onChange={(e) => setConfig({ ...config, custom_css: e.target.value })}
                  placeholder={`/* Ejemplo de CSS personalizado */
.btn-primary {
  border-radius: 20px;
}

.card {
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
}`}
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Agrega CSS personalizado para modificar estilos específicos. 
                  Los cambios se aplicarán globalmente.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PDF Responsiva Tab */}
        <TabsContent value="pdf">
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* PDF Configuration - Left Column */}
              <Card>
                <CardHeader>
                  <CardTitle>Información de la Empresa</CardTitle>
                  <CardDescription>
                    Datos que aparecerán en el encabezado del PDF
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Logo Upload */}
                  <div className="space-y-3">
                    <Label>Logo del PDF</Label>
                    <div className="flex items-center gap-4">
                      {pdfConfig.logo_url ? (
                        <img src={pdfConfig.logo_url} alt="Logo PDF" className="h-12 w-auto object-contain bg-white rounded p-2 border" />
                      ) : (
                        <div className="h-12 w-20 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground border">
                          Por defecto
                        </div>
                      )}
                      <input
                        ref={pdfLogoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append('file', file);
                            try {
                              const res = await api().post('/config/upload-logo', formData);
                              setPdfConfig({...pdfConfig, logo_url: res.data.url});
                              toast.success('Logo actualizado');
                            } catch (err) {
                              toast.error('Error al subir logo');
                            }
                          }
                        }}
                      />
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => pdfLogoInputRef.current?.click()}>
                          <Upload className="h-4 w-4 mr-1" />
                          Cambiar
                        </Button>
                        {pdfConfig.logo_url && (
                          <Button variant="ghost" size="sm" onClick={() => setPdfConfig({...pdfConfig, logo_url: ''})}>
                            Quitar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nombre de Empresa</Label>
                      <Input
                        value={pdfConfig.company_name}
                        onChange={(e) => setPdfConfig({...pdfConfig, company_name: e.target.value})}
                        placeholder="CM Idiomas"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Teléfono</Label>
                      <Input
                        value={pdfConfig.company_phone || ''}
                        onChange={(e) => setPdfConfig({...pdfConfig, company_phone: e.target.value})}
                        placeholder="+52 123 456 7890"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Dirección</Label>
                    <Input
                      value={pdfConfig.company_address || ''}
                      onChange={(e) => setPdfConfig({...pdfConfig, company_address: e.target.value})}
                      placeholder="Av. Principal #123, Ciudad, Estado"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      value={pdfConfig.company_email || ''}
                      onChange={(e) => setPdfConfig({...pdfConfig, company_email: e.target.value})}
                      placeholder="contacto@empresa.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Color del Encabezado</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="color"
                        value={pdfConfig.header_color}
                        onChange={(e) => setPdfConfig({...pdfConfig, header_color: e.target.value})}
                        className="w-14 h-9 p-1"
                      />
                      <Input
                        value={pdfConfig.header_color}
                        onChange={(e) => setPdfConfig({...pdfConfig, header_color: e.target.value})}
                        className="w-24"
                      />
                      <Button variant="ghost" size="sm" onClick={() => setPdfConfig({...pdfConfig, header_color: '#00B2A9'})}>
                        Reset
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Columns Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle>Columnas de la Tabla</CardTitle>
                  <CardDescription>
                    Selecciona qué columnas mostrar en la tabla de equipos
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: 'show_quantity', label: 'Cantidad' },
                      { key: 'show_description', label: 'Descripción' },
                      { key: 'show_serial', label: 'No. Serie' },
                      { key: 'show_location', label: 'Ubicación' },
                      { key: 'show_condition', label: 'Condición' }
                    ].map(col => (
                      <label key={col.key} className="flex items-center gap-2 p-2 bg-muted/30 rounded cursor-pointer hover:bg-muted/50">
                        <input
                          type="checkbox"
                          checked={pdfConfig[col.key] !== false}
                          onChange={(e) => setPdfConfig({...pdfConfig, [col.key]: e.target.checked})}
                          className="rounded"
                        />
                        <span className="text-sm">{col.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Custom Columns */}
                  <div className="border-t pt-4">
                    <Label className="mb-2 block">Columnas Personalizadas</Label>
                    <div className="flex gap-2 mb-3">
                      <Input
                        value={newColumnName}
                        onChange={(e) => setNewColumnName(e.target.value)}
                        placeholder="Nombre de columna..."
                        className="flex-1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newColumnName.trim()) {
                            setPdfConfig({
                              ...pdfConfig,
                              custom_columns: [...(pdfConfig.custom_columns || []), { name: newColumnName.trim(), enabled: true }]
                            });
                            setNewColumnName('');
                          }
                        }}
                      />
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (newColumnName.trim()) {
                            setPdfConfig({
                              ...pdfConfig,
                              custom_columns: [...(pdfConfig.custom_columns || []), { name: newColumnName.trim(), enabled: true }]
                            });
                            setNewColumnName('');
                          }
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {(pdfConfig.custom_columns || []).length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {(pdfConfig.custom_columns || []).map((col, idx) => (
                          <div key={idx} className="flex items-center gap-1 px-3 py-1 bg-muted rounded-full text-sm">
                            <input
                              type="checkbox"
                              checked={col.enabled !== false}
                              onChange={(e) => {
                                const newCols = [...pdfConfig.custom_columns];
                                newCols[idx].enabled = e.target.checked;
                                setPdfConfig({...pdfConfig, custom_columns: newCols});
                              }}
                              className="rounded w-3 h-3"
                            />
                            <span>{col.name}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0"
                              onClick={() => {
                                setPdfConfig({
                                  ...pdfConfig,
                                  custom_columns: pdfConfig.custom_columns.filter((_, i) => i !== idx)
                                });
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Signature Fields & Disclaimer */}
              <Card>
                <CardHeader>
                  <CardTitle>Firmas y Texto Legal</CardTitle>
                  <CardDescription>
                    Configura los campos de firma y el texto de responsabilidad
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Signature Fields */}
                  <div className="space-y-3">
                    <Label>Campos de Firma</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newSignatureLabel}
                        onChange={(e) => setNewSignatureLabel(e.target.value)}
                        placeholder="Ej: TESTIGO, VO.BO. DIRECTOR..."
                        className="flex-1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (newSignatureLabel.trim()) {
                              setPdfConfig(prev => ({
                                ...prev,
                                signature_fields: [...(prev.signature_fields || []), { label: newSignatureLabel.trim(), type: 'custom' }]
                              }));
                              setNewSignatureLabel('');
                            }
                          }
                        }}
                        data-testid="new-signature-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="shrink-0"
                        data-testid="add-signature-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Add signature clicked, value:', newSignatureLabel);
                          if (newSignatureLabel.trim()) {
                            setPdfConfig(prev => {
                              const newFields = [...(prev.signature_fields || []), { label: newSignatureLabel.trim(), type: 'custom' }];
                              console.log('New signature fields:', newFields);
                              return {
                                ...prev,
                                signature_fields: newFields
                              };
                            });
                            setNewSignatureLabel('');
                          }
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(pdfConfig.signature_fields || []).map((field, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded">
                          <span className="flex-1 text-sm">{field.label}</span>
                          <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
                            {field.type === 'entrega' ? 'Auto: Responsable' : field.type === 'recibe' ? 'Auto: Cliente' : 'Manual'}
                          </span>
                          {field.type === 'custom' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => {
                                setPdfConfig({
                                  ...pdfConfig,
                                  signature_fields: pdfConfig.signature_fields.filter((_, i) => i !== idx)
                                });
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Disclaimer Text */}
                  <div className="space-y-2 border-t pt-4">
                    <Label>Texto de Responsabilidad</Label>
                    <Textarea
                      value={pdfConfig.disclaimer_text}
                      onChange={(e) => setPdfConfig({...pdfConfig, disclaimer_text: e.target.value})}
                      placeholder="Por este conducto, la persona receptora se hace responsable..."
                      rows={4}
                    />
                  </div>

                  {/* Save Button */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button 
                      onClick={async () => {
                        setSavingPdf(true);
                        try {
                          await api().post('/config/pdf-template', pdfConfig);
                          toast.success('Configuración de PDF guardada');
                        } catch (err) {
                          toast.error('Error al guardar');
                        } finally {
                          setSavingPdf(false);
                        }
                      }} 
                      disabled={savingPdf}
                      className="btn-primary"
                    >
                      {savingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Guardar Configuración
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* PDF Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Vista Previa
                  </CardTitle>
                  <CardDescription>
                    Así se verá la responsiva de equipo
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-white text-black p-4 rounded-lg border shadow-lg text-xs max-h-[500px] overflow-auto">
                    {/* Logo - ALWAYS CENTERED */}
                    <div className="text-center mb-2">
                      {pdfConfig.logo_url ? (
                        <img src={pdfConfig.logo_url} alt="Logo" className="h-8 mx-auto object-contain" />
                      ) : (
                        <img src="https://cmidiomas.com/images/logo-default-246x44.png" alt="Logo" className="h-8 mx-auto object-contain" />
                      )}
                    </div>
                    
                    {/* Company info BELOW logo */}
                    {(pdfConfig.company_address || pdfConfig.company_phone || pdfConfig.company_email) && (
                      <div className="text-center text-gray-500 mb-2" style={{fontSize: '8px'}}>
                        {[pdfConfig.company_address, pdfConfig.company_phone && `Tel: ${pdfConfig.company_phone}`, pdfConfig.company_email].filter(Boolean).join(' | ')}
                      </div>
                    )}
                    
                    <div className="text-gray-500 mb-1" style={{fontSize: '8px'}}>CMI-FO-E05 | Folio: B964BD4D</div>
                    <h1 className="text-sm font-bold text-center mb-2">ENTREGA-RECEPCIÓN DE EQUIPO</h1>
                    
                    <div className="mb-2 space-y-0.5" style={{fontSize: '9px'}}>
                      <div><b>Evento:</b> Conferencia Anual</div>
                      <div><b>Cliente:</b> Cliente Ejemplo</div>
                    </div>
                    
                    {/* Dynamic Table */}
                    <table className="w-full border-collapse mb-2" style={{fontSize: '8px'}}>
                      <thead>
                        <tr style={{ backgroundColor: pdfConfig.header_color }}>
                          <th className="border p-1 text-white">No.</th>
                          {pdfConfig.show_quantity !== false && <th className="border p-1 text-white">Cant.</th>}
                          {pdfConfig.show_description !== false && <th className="border p-1 text-white text-left">Descripción</th>}
                          {pdfConfig.show_serial && <th className="border p-1 text-white">Serie</th>}
                          {pdfConfig.show_location && <th className="border p-1 text-white">Ubicación</th>}
                          {pdfConfig.show_condition && <th className="border p-1 text-white">Condición</th>}
                          {(pdfConfig.custom_columns || []).filter(c => c.enabled !== false).map((col, i) => (
                            <th key={i} className="border p-1 text-white">{col.name}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border p-1 text-center">1</td>
                          {pdfConfig.show_quantity !== false && <td className="border p-1 text-center">2</td>}
                          {pdfConfig.show_description !== false && <td className="border p-1">Laptop Dell</td>}
                          {pdfConfig.show_serial && <td className="border p-1 text-center">LAP-001</td>}
                          {pdfConfig.show_location && <td className="border p-1 text-center">Almacén</td>}
                          {pdfConfig.show_condition && <td className="border p-1 text-center">Bueno</td>}
                          {(pdfConfig.custom_columns || []).filter(c => c.enabled !== false).map((_, i) => (
                            <td key={i} className="border p-1 text-center">-</td>
                          ))}
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="border p-1 text-center">2</td>
                          {pdfConfig.show_quantity !== false && <td className="border p-1 text-center">1</td>}
                          {pdfConfig.show_description !== false && <td className="border p-1">Proyector</td>}
                          {pdfConfig.show_serial && <td className="border p-1 text-center">PRY-002</td>}
                          {pdfConfig.show_location && <td className="border p-1 text-center">Sala 3</td>}
                          {pdfConfig.show_condition && <td className="border p-1 text-center">Regular</td>}
                          {(pdfConfig.custom_columns || []).filter(c => c.enabled !== false).map((_, i) => (
                            <td key={i} className="border p-1 text-center">-</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                    
                    <p className="text-gray-600 mb-3" style={{fontSize: '8px'}}>
                      {pdfConfig.disclaimer_text?.substring(0, 150)}...
                    </p>
                    
                    {/* Dynamic Signatures */}
                    <div className="grid gap-2 mt-3" style={{ gridTemplateColumns: `repeat(${(pdfConfig.signature_fields || []).length || 2}, 1fr)`, fontSize: '8px' }}>
                      {(pdfConfig.signature_fields || []).map((field, idx) => (
                        <div key={idx}>
                          <div className="font-bold pb-1 mb-1" style={{ borderBottom: `1px solid ${pdfConfig.header_color}` }}>{field.label}</div>
                          <div>Nombre: ___________</div>
                          <div>Firma: ___________</div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="text-gray-500 text-center mt-3 pt-2 border-t" style={{fontSize: '7px'}}>
                      {pdfConfig.company_name} | Documento generado automáticamente
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirmar Restauración
            </DialogTitle>
            <DialogDescription>
              {restoreType === 'style' && 'Esta acción restaurará todos los estilos de la aplicación a sus valores predeterminados.'}
              {restoreType === 'login' && 'Esta acción restaurará la configuración de la pantalla de login a sus valores predeterminados.'}
              {restoreType === 'manual' && 'Esta acción eliminará el manual personalizado y restaurará el manual generado automáticamente por el sistema.'}
              <br /><br />
              <strong>¿Estás seguro de que deseas continuar?</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={executeRestore}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Sí, Restaurar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StylePage;
